import { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SuperTableV2 from "../SuperTableV2";

// Tabla + buscador client-side (P15/RF-15) + contador + menú de elipsis (RF-14/RF-16) del módulo
// "Iniciativas de IA" (spec 008. Incremento 1: registro. Incrementos 2-3: flujo de Champion, plan.md §14.3
// — T-APP-22). **Se reutiliza el mismo componente para las 3 pestañas** ("Iniciativas", "Transiciones a
// F2", "Iniciativas Planeadas") — cambia el prop `variante` y los datos filtrados que llega desde
// views/IniciativasIA.jsx (P21, filtrado client-side sobre el mismo listado), no la tabla.
//
// R-8 (recorte del menú de elipsis por contenedores con overflow): se resuelve reusando SuperTableV2
// tal cual lo hace views/Projects.jsx — el wrapper "table-ddl" que SuperTableV2 aplica siempre ya tiene
// en src/css/custom.scss la regla `.table-ddl .dx-datagrid-rowsview { overflow: visible; }`, que es
// exactamente el mismo mecanismo que evita el recorte en la tabla de Proyectos. No hace falta portal
// propio: se decide reusar el patrón existente en vez de inventar uno nuevo. **No se toca en este ciclo.**
//
// La búsqueda es propia (no la SearchPanel nativa de DevExtreme, por eso `noSearch`) porque RF-15 pide
// filtrar también por Código y por el nombre traducido del Estado, y el Código no es un dataField propio
// de ninguna columna (vive dentro del customRender de "Nombre").
const ESTADOS_NO_EDITABLE = ["ENVIADO", "HABILITADO"];
const ESTADO_ELIMINABLE = "GUARDADO";
const ESTADO_HABILITADO = "HABILITADO";

export const formatFecha = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// RF-55/P17: la variante Champion (Habilitar/Devolver) es SOLO comodidad visual — el 403 real lo pone
// SP_INIA_CAMBIAR_ESTADO/SP_INIA_HABILITAR. Comparación insensible a caja (defecto 1 del recorrido del
// incremento 1: los uniqueidentifier de SQL Server vienen en MAYÚSCULAS): no repetir un `===` directo.
const esChampionAsignado = (row, currentUserId) =>
  !!row.IdChampion &&
  !!currentUserId &&
  String(row.IdChampion).toUpperCase() === String(currentUserId).toUpperCase();

// Regla A (spec 009, RF-9/RF-10/T-APP-11): comodidad de UI — la autoridad real vive en SP_INIA_ELIMINAR
// (plan.md §1.1 de 009). Comparación insensible a caja, mismo criterio que esChampionAsignado.
//
// `CreadoPor` llega como la columna 20 de SP_INIA_LISTAR, agregada de forma aditiva en este mismo
// incremento (U001 de 009) precisamente para que esta condición pueda evaluarse. NO se compara contra
// IdPostulante: spec.md §1.1(3) de 009 lo prohíbe para esta regla, porque quien registra y el postulante
// pueden ser personas distintas (asimetría deliberada frente a P16 de 008).
const esCreador = (row, currentUserId) =>
  !!row.CreadoPor &&
  !!currentUserId &&
  String(row.CreadoPor).toUpperCase() === String(currentUserId).toUpperCase();

// "Dueño" de la iniciativa según P16 del spec 008: CreadoPor **o** IdPostulante. Es la misma regla que ya
// aplica SP_INIA_CAMBIAR_ESTADO para ENVIAR/CANCELAR_ENVIO, y desde el 2026-08-03 también SP_INIA_ACTUALIZAR
// (U003 de 009): editar exige ser dueño. Cubre el caso de que un asistente registre la iniciativa de un
// gerente — los dos pueden corregirla.
//
// Ojo con la asimetría, es deliberada: **eliminar** NO usa esta función (usa esCreador + Champion), y
// **editar** no admite al Champion. Si algo está mal, el Champion devuelve la iniciativa con motivo; no
// reescribe lo que está evaluando.
//
// Comodidad de UI: la autoridad real es el SP. Comparación insensible a caja por la misma razón que arriba.
const esDueno = (row, currentUserId) =>
  esCreador(row, currentUserId) ||
  (!!row.IdPostulante &&
    !!currentUserId &&
    String(row.IdPostulante).toUpperCase() === String(currentUserId).toUpperCase());

const IniciativasTable = ({
  iniciativas,
  onEdit,
  onDelete,
  onAcciones,
  onVerDetalle,
  variante = "gestion", // "gestion" | "champion" | "consulta" (T-APP-22/plan.md §14.3)
  currentUserId,
}) => {
  const [t] = useTranslation("iniciativasIA");
  const [search, setSearch] = useState("");
  // RF-10/T-APP-11 (spec 009): "Champion de la organización" (esChampion del store), NO
  // esChampionAsignado de esta fila — para borrar importa ser Champion en general, no el asignado a ESTA
  // iniciativa (que ni siquiera aplica a filas GUARDADO sin envío, plan.md §2.6 de 009).
  const esChampionOrganizacion = useSelector((state) => !!state.iniciativasIA?.esChampion);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return iniciativas;
    return iniciativas.filter((row) =>
      [row.Nombre, row.Codigo, row.NombreClubhouse, row.NombrePostulante, row.NombreTipoIA, row.NombreEstado]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().indexOf(q) !== -1)
    );
  }, [iniciativas, search]);

  const columns = useMemo(
    () => [
      {
        caption: t("iniciativasIA_col_name"),
        selector: "Nombre",
        width: 320,
        customRender: (row) => {
          const data = row.data;
          // Editar exige DOS cosas: estado editable **y** ser dueño (P16). Antes solo miraba el estado, así
          // que cualquiera de la organización podía abrir y modificar la iniciativa de otro — hueco
          // detectado en pruebas manuales (2026-08-03) y cerrado en SP_INIA_ACTUALIZAR (U003 de 009).
          const estadoPermiteEditar = !ESTADOS_NO_EDITABLE.includes(data.CodigoEstado);
          const editable = estadoPermiteEditar && esDueno(data, currentUserId);
          // Dos motivos distintos para deshabilitar "Editar" -> dos mensajes distintos. Con uno solo, a
          // quien no es dueño se le diría que el problema es el estado, que es falso y confunde.
          const editDisabledTitle = !estadoPermiteEditar
            ? t("iniciativasIA_menu_edit_disabled_title")
            : t("iniciativasIA_menu_edit_disabled_not_owner");
          // RF-9/RF-10 (spec 009): comodidad de UI — solo el autor o un Champion de la organización ven
          // "Eliminar". La autoridad real está en SP_INIA_ELIMINAR (RF-9).
          const deletable =
            data.CodigoEstado === ESTADO_ELIMINABLE &&
            (esCreador(data, currentUserId) || esChampionOrganizacion);
          // RF-37: la opción "Acciones de la iniciativa" se oculta si el estado es Habilitado, en
          // cualquier pestaña. En la variante Champion además exige ser el Champion asignado a ESTA fila
          // (P17/RF-55) — comodidad visual, la autoridad real está en el SP.
          const accionesVisible =
            data.CodigoEstado !== ESTADO_HABILITADO &&
            ((variante === "gestion" && !!onAcciones) ||
              (variante === "champion" && !!onAcciones && esChampionAsignado(data, currentUserId)));
          // RF-42/CA-40: el nombre abre el formulario si la iniciativa aún es editable, o la vista de
          // detalle de solo lectura si no lo es (Enviado/Habilitado, y por construcción las 2 pestañas del
          // Champion, que solo listan esos dos estados).
          const handleNombreClick = () => {
            if (editable && onEdit) onEdit(data);
            else if (onVerDetalle) onVerDetalle(data);
          };
          return (
            <div className="d-flex align-items-center dropdown-in-table">
              {variante !== "consulta" && (
                <Dropdown onClick={(e) => e.stopPropagation()}>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    size="sm"
                    className="dropdown-btn-ellipsis"
                  >
                    <i className="fa fa-ellipsis-h"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      disabled={!editable}
                      title={!editable ? editDisabledTitle : undefined}
                      onClick={() => onEdit && onEdit(data)}
                    >
                      <i className="fa fa-edit mr-2 text-info"></i>
                      {t("iniciativasIA_menu_edit")}
                    </Dropdown.Item>
                    {deletable && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item className="text-danger" onClick={() => onDelete && onDelete(data)}>
                          <i className="fa fa-trash mr-2"></i>
                          {t("iniciativasIA_menu_delete")}
                        </Dropdown.Item>
                      </>
                    )}
                    {accionesVisible && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => onAcciones(data)}>
                          <i className="fa fa-random mr-2 text-primary"></i>
                          {t("iniciativasIA_menu_acciones")}
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <span
                className="ml-2"
                style={{ maxWidth: "260px", cursor: "pointer" }}
                title={data.Nombre}
                onClick={handleNombreClick}
              >
                {data.Nombre}
                <small className="d-block text-muted">{data.Codigo}</small>
              </span>
            </div>
          );
        },
      },
      {
        caption: t("iniciativasIA_col_date"),
        selector: "Fecha",
        width: 110,
        customRender: (row) => <span>{formatFecha(row.data.Fecha)}</span>,
      },
      { caption: t("iniciativasIA_col_clubhouse"), selector: "NombreClubhouse", width: 200 },
      { caption: t("iniciativasIA_col_applicant"), selector: "NombrePostulante", width: 200 },
      { caption: t("iniciativasIA_col_ai_type"), selector: "NombreTipoIA", width: 160 },
      {
        caption: t("iniciativasIA_col_status"),
        selector: "NombreEstado",
        width: 130,
        customRender: (row) => (
          <span className={`badge ${row.data.ClaseCss}`}>{row.data.NombreEstado}</span>
        ),
      },
    ],
    // onEdit/onDelete/onAcciones/onVerDetalle no entran a la lista a propósito (mismo patrón ya existente
    // desde el Incremento 1): son callbacks que solo leen el `row` recibido al invocarlos y disparan
    // setState/dispatch estables, así que una referencia "vieja" capturada por el memo sigue siendo
    // correcta. `variante`/`currentUserId` sí determinan qué se renderiza (accionesVisible) y si cambiaran
    // en caliente en la MISMA instancia habría que recalcular — hoy no ocurre (cada pestaña monta su propia
    // instancia con un `variante` fijo), pero se listan por seguridad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, variante, currentUserId, esChampionOrganizacion]
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <p className="mb-0">
          {t("iniciativasIA_counter_prefix")} <b className="text-primary">({filtered.length})</b>{" "}
          {t("iniciativasIA_counter_suffix")}
        </p>
        <div className="input-group input-group-sm mr-3" style={{ maxWidth: "280px" }}>
          <span className="input-group-text bg-white">
            <i className="fa fa-search text-muted"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder={t("iniciativasIA_search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filtered.length ? (
        <div className="table-with-dropdown">
          <SuperTableV2 data={filtered} columns={columns} noSearch />
        </div>
      ) : (
        <p className="text-center text-muted py-4">{t("iniciativasIA_search_noresults")}</p>
      )}
    </>
  );
};

export default IniciativasTable;
